import { useState } from "react";
import {Heading, HStack, Image, Text, VStack, Box, background} from "@chakra-ui/react";
import { RadioGroup, RadioOption } from "./Radio";
import { ProgressCircle } from "./Progress";
import { Info } from "./Info";
import { Resultaat } from "./Resultaat";
import './Vragenlijst.css';
import './nav-button.css';

export default function Vragenlijst() {

    const Score: Record<string, number> = {
        nooit: 0,
        soms: 1,
        vaak: 2,
        altijd: 3
    };

    interface VragenlijstType {
        naam?: string,
        leeftijd?: number,
        vragen: VraagType[],
        huidigeVraag: number
    }

    interface VraagType {
        id?: string, // URI
        vraagstelling: string,
        toelichting?: string,
        antwoord?: number
    }

    const vragenlijst: VragenlijstType = {
        vragen: [
            { vraagstelling: "Ik let onvoldoende op details bij mijn werk.", toelichting: "Dit is een kernsymptoom van aandachtstekort volgens de DSM-5. Moeite met details kan wijzen op problemen met de executieve functies in de hersenen." },
            { vraagstelling: "Wanneer ik zit, friemel ik met mijn handen of voeten.", toelichting: "Friemelen en rusteloos bewegen zijn typische tekenen van hyperactiviteit. Dit komt door een overschot aan energie dat moeilijk onder controle te houden is." },
            { vraagstelling: "Ik maak slordige fouten in mijn werk.", toelichting: "Achteloze fouten ontstaan doordat de aandacht onvoldoende gefocust blijft. Dit is een belangrijk DSM-5 criterium voor aandachtsproblemen." },
            { vraagstelling: "Ik zit te wiebelen en te draaien in mijn stoel.", toelichting: "Wiebelen is een klassiek hyperactiviteitssymptoom. Het lichaam heeft moeite om stil te blijven zitten, wat kan leiden tot onrust in werk- of leersituaties." },
            { vraagstelling: "Wanneer ik met iets bezig ben, kan ik er met mijn aandacht slecht bij blijven.", toelichting: "Moeite om aandacht vast te houden bij taken is een hoofdkenmerk van ADHD. De hersenen hebben moeite om focus te behouden over langere tijd." },
            { vraagstelling: "Ik sta snel op van mijn stoel in situaties waarin verwacht wordt dat ik netjes blijf zitten.", toelichting: "Onvermogen om stil te blijven zitten in geschikte situaties is een duidelijk hyperactiviteitssignaal volgens de diagnostische criteria." },
            { vraagstelling: "Ik luister slecht wanneer anderen iets tegen mij zeggen.", toelichting: "Lijken niet te luisteren komt door aandachtsregulatieproblemen. De hersenen hebben moeite om zich te concentreren op gesproken informatie." },
            { vraagstelling: "Ik voel me rusteloos.", toelichting: "Innerlijke rusteloosheid is een belangrijke indicator van hyperactiviteit. Dit gevoel komt door verhoogde activiteit in bepaalde hersengebieden." },
            { vraagstelling: "Ik verveel me snel.", toelichting: "Snel vervelen hangt samen met behoefte aan continue stimulatie. Dit kan wijzen op problemen met dopamine-regulatie in de hersenen." },
            { vraagstelling: "Ik heb moeite aanwijzingen op te volgen.", toelichting: "Moeite met het opvolgen van instructies is een DSM-5 criterium dat wijst op problemen met werkgeheugen en executieve functies." },
            { vraagstelling: "Karweitjes of werk waar ik aan begin, maak ik niet af.", toelichting: "Taken niet afmaken is een belangrijk aandachtssymptoom. Dit komt door problemen met taakvolharding en planning." },
            { vraagstelling: "Ik kan me moeilijk ontspannen in mijn vrije tijd.", toelichting: "Moeite met ontspannen is een hyperactiviteitskenmerk. De innerlijke motor blijft doordraaien, zelfs in rustsituaties." },
            { vraagstelling: "In mijn vakantie of vrije tijd zoek ik een omgeving met drukte en lawaai.", toelichting: "Behoefte aan prikkels kan wijzen op onderactiviteit in bepaalde hersendelen die compensatie zoeken door externe stimulatie." },
            { vraagstelling: "Ik kan mijn bezigheden of taken moeilijk organiseren.", toelichting: "Organisatieproblemen zijn een kernsymptoom van ADHD volgens de DSM-5, gerelateerd aan zwakkere executieve functies." },
            { vraagstelling: "Ik ben voortdurend 'in de weer', alsof ik 'door een motor word aangedreven'.", toelichting: "Het 'motorisch aangedreven worden' is letterlijk een DSM-5 criterium voor hyperactiviteit en een zeer specifiek ADHD-kenmerk." },
            { vraagstelling: "Ik probeer onder bezigheden uit te komen waarop ik me langere tijd moet concentreren.", toelichting: "Vermijding van langdurige mentale inspanning is een DSM-5 criterium dat wijst op problemen met aandachtsvolharding." },
            { vraagstelling: "Ik praat aan één stuk door.", toelichting: "Veel praten is een impulsiviteitssymptoom. De remfunctie in de hersenen heeft moeite om spraakimpulsen te controleren." },
            { vraagstelling: "Ik raak dingen kwijt die ik nodig heb voor taken of bezigheden.", toelichting: "Vaak dingen kwijtraken is een DSM-5 criterium voor aandachtsproblemen, gerelateerd aan zwak werkgeheugen en desorganisatie." },
            { vraagstelling: "Ik geef antwoord voordat vragen zijn afgemaakt.", toelichting: "Te snel antwoorden is een klassiek impulsiviteitssymptoom. De hersenen hebben moeite met het remmen van spontane reacties." },
            { vraagstelling: "Ik ben snel afgeleid.", toelichting: "Afleidbaarheid door externe prikkels is een DSM-5 kerncriterium voor aandachtsproblemen. Het aandachtsfilter werkt onvoldoende." },
            { vraagstelling: "Ik vind het moeilijk op mijn beurt te wachten.", toelichting: "Ongeduld is een impulsiviteitsindicator volgens de diagnostische criteria. Dit komt door zwakke impulscontrole in de hersenen." },
            { vraagstelling: "Ik ben vergeetachtig bij alledaagse bezigheden.", toelichting: "Vergeetachtigheid in dagelijkse activiteiten is een DSM-5 criterium dat wijst op werkgeheugenproblemen, een belangrijke executieve functie." },
            { vraagstelling: "Ik onderbreek anderen of val ze in de rede.", toelichting: "Anderen onderbreken is een specifiek impulsiviteitssymptoom volgens de DSM-5. De remfunctie heeft moeite sociale impulsen te controleren." },
        ],
        huidigeVraag: 0
    }

    const [vragenlijstState, setVragenlijstState] = useState(vragenlijst)
    const huidigeVraag: VraagType = vragenlijstState.vragen[vragenlijstState.huidigeVraag]
    const aantalVragen = vragenlijstState.vragen.length
    const progress = () => {
        const beantwoord = vragenlijstState.vragen.filter(vraag => vraag.antwoord !== undefined).length
        return beantwoord * 100 / aantalVragen;
    }

    const [toonResultaten, setToonResultaten] = useState(false)

    const geefAntwoord = (antwoord: string) => {
        setVragenlijstState(prev => {
            let newState = { ...prev,
                vragen: [...prev.vragen],
                huidigeVraag: Math.min(prev.huidigeVraag + 1, prev.vragen.length - 1)
            }
            const scoreWaarde = Score[antwoord];
            console.log("antwoord: " + antwoord + " , score: " + scoreWaarde)
            newState.vragen[prev.huidigeVraag].antwoord = scoreWaarde
            return newState
        })
    }

    const vorigeVraag = () => {
        setVragenlijstState((prev)  => {
            return { ...prev,
                huidigeVraag: prev.huidigeVraag - 1
            }
        })
    }

    const volgendeVraag = () => {
        if (vragenlijstState.huidigeVraag < vragenlijstState.vragen.length - 1) {
            setVragenlijstState((prev)  => {
                return { ...prev,
                    huidigeVraag: prev.huidigeVraag + 1
                }
            })
        } else {
            setToonResultaten(true)
        }
    }

    if (!toonResultaten) {
        return (
            <VStack align="center">
                <div className="vraagstelling">{huidigeVraag.vraagstelling}</div>
                <Info size={100}>{huidigeVraag.toelichting}</Info>
                <RadioGroup onChange={(antwoord: string) => geefAntwoord(antwoord)} selected={huidigeVraag.antwoord !== undefined ? Object.keys(Score).find(key => Score[key] === huidigeVraag.antwoord) : undefined}>
                    <RadioOption value="nooit">Nooit of zelden</RadioOption>
                    <RadioOption value="soms">Soms</RadioOption>
                    <RadioOption value="vaak">Vaak</RadioOption>
                    <RadioOption value="altijd">Erg vaak</RadioOption>
                </RadioGroup>
                <ProgressCircle percentage={progress()} size={200}/>
                <HStack>
                    <button className="nav-button" disabled={(vragenlijstState.huidigeVraag === 0)} onClick={vorigeVraag}>Vorige vraag</button>
                    <button className="nav-button" disabled={huidigeVraag.antwoord === undefined}
                            onClick={volgendeVraag}>{vragenlijstState.huidigeVraag < aantalVragen - 1 ? "Volgende vraag" : "Resultaat"}</button>
                </HStack>
            </VStack>
        )
    } else {
        return (
			<Resultaat vragen={vragenlijstState.vragen} onTerugNaarVragen={() => setToonResultaten(false)} />
		)
    }
}

